import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangleIcon, ArrowDownIcon, CheckIcon, ChevronRightIcon, CreditCardIcon, CrownIcon, LogOutIcon, SettingsIcon, ShieldIcon, SwordIcon, UserIcon, WalletIcon, WrenchIcon } from 'lucide-react';
import type { AccountProfile, CoinPackage, DashboardIntent, GameCharacter, TransactionRecord, VanguardCoinBalance } from '../../data/contracts';
type ActionSurface = 'purchase' | 'withdrawal' | 'unstuck' | 'password' | null;
interface DashboardPortalProps {
  account: AccountProfile;
  balance: VanguardCoinBalance;
  characters: GameCharacter[];
  coinPackages: CoinPackage[];
  transactions: TransactionRecord[];
  initialIntent: DashboardIntent;
  onLogout: () => void;
}
const tabs: Array<{
  id: DashboardIntent;
  label: string;
  icon: typeof UserIcon;
}> = [{
  id: 'overview',
  label: 'Resumen de Cuenta',
  icon: UserIcon
}, {
  id: 'buy',
  label: 'Comprar V Coins',
  icon: CreditCardIcon
}, {
  id: 'withdraw',
  label: 'Retirar a USDT',
  icon: WalletIcon
}, {
  id: 'unstuck',
  label: 'Destrabar Personaje',
  icon: WrenchIcon
}, {
  id: 'settings',
  label: 'Ajustes',
  icon: SettingsIcon
}];
export function DashboardPortal({
  account,
  balance,
  characters,
  coinPackages,
  transactions,
  initialIntent,
  onLogout
}: DashboardPortalProps) {
  const [activeTab, setActiveTab] = useState<DashboardIntent>(initialIntent);
  const [selectedPackage, setSelectedPackage] = useState(coinPackages[1]?.id || coinPackages[0]?.id || '');
  const [selectedCharacterId, setSelectedCharacterId] = useState(characters[0]?.id || '');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [notice, setNotice] = useState<ActionSurface>(null);
  useEffect(() => {
    setActiveTab(initialIntent);
  }, [initialIntent]);
  const selectedCharacter = useMemo(() => characters.find(character => character.id === selectedCharacterId), [characters, selectedCharacterId]);
  const usdtReceive = withdrawAmount && balance.exchangeRateToUsdt ? (Number(withdrawAmount) * balance.exchangeRateToUsdt).toFixed(2) : '0.00';
  const showIntegrationNotice = (surface: Exclude<ActionSurface, null>) => {
    setNotice(surface);
  };
  return <main className="flex-grow pt-32 pb-24 relative" data-id="element-42">
      <div className="absolute inset-0 z-0" data-id="element-43">
        <img src="https://cdn.magicpatterns.com/uploads/aj6WysGqM5MqYSqQzX8AWP/ChatGPT_Image_Mar_25,_2026,_10_00_56_AM.png" alt="Fantasy Landscape" className="w-full h-full object-cover" data-id="element-44" />
        <div className="absolute inset-0 bg-darkBlue/70" data-id="element-45" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-46">
        <div className="flex flex-col md:flex-row gap-8" data-id="element-47">
          <aside className="w-full md:w-64 flex-shrink-0" data-id="element-48">
            <div className="glass-panel rounded-xl p-4 sticky top-32" data-id="element-49">
              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-white/10 px-2" data-id="element-50">
                <div className="w-12 h-12 rounded-full bg-darkerBlue border border-gold/30 flex items-center justify-center flex-shrink-0" data-id="element-51">
                  <UserIcon className="w-6 h-6 text-gold" data-id="element-52" />
                </div>
                <div className="overflow-hidden" data-id="element-53">
                  <h2 data-testid="dashboard-username" data-field="account.username" className="font-bold text-lg truncate" data-id="element-54">
                    {account.username}
                  </h2>
                  <p className="text-xs text-gold flex items-center gap-1" data-id="element-55">
                    <CrownIcon className="w-3 h-3" data-id="element-56" />
                    <span data-testid="dashboard-vcoin-balance-summary" data-field="account.vCoinBalance" data-id="element-57">
                      {balance.available?.toLocaleString() ?? '—'} V Coins
                    </span>
                  </p>
                </div>
              </div>

              <nav aria-label="Dashboard navigation" className="flex md:flex-col overflow-x-auto md:overflow-visible gap-2 pb-2 md:pb-0 scrollbar-hide" data-id="element-58">
                {tabs.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return <button key={tab.id} type="button" data-testid={`dashboard-tab-${tab.id}`} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all whitespace-nowrap md:whitespace-normal text-left ${isActive ? 'bg-gold/10 text-gold border border-gold/20 shadow-[inset_0_0_15px_rgba(212,175,55,0.1)]' : 'text-gray-400 hover:bg-white/5 hover:text-white border border-transparent'}`} data-id="element-59">
                      <Icon className={`w-5 h-5 ${isActive ? 'text-gold' : 'text-gray-500'}`} data-id="element-60" />
                      <span className="font-medium text-sm" data-id="element-61">{tab.label}</span>
                      {isActive && <ChevronRightIcon className="w-4 h-4 ml-auto hidden md:block" data-id="element-62" />}
                    </button>;
              })}
                <button type="button" data-testid="dashboard-logout" onClick={onLogout} className="flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10 hover:text-red-300 mt-auto md:mt-4" data-id="element-63">
                  <LogOutIcon className="w-5 h-5" data-id="element-64" />
                  <span className="font-medium text-sm" data-id="element-65">Cerrar Sesión</span>
                </button>
              </nav>
            </div>
          </aside>

          <section className="flex-grow min-w-0" aria-live="polite" data-id="element-66">
            <AnimatePresence mode="wait" data-id="element-67">
              <motion.div key={activeTab} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-68">
                {activeTab === 'overview' && <OverviewTab account={account} balance={balance} characters={characters} transactions={transactions} data-id="element-69" />}
                {activeTab === 'buy' && <PurchaseTab coinPackages={coinPackages} selectedPackage={selectedPackage} onPackageSelect={setSelectedPackage} onRequest={() => showIntegrationNotice('purchase')} data-id="element-70" />}
                {activeTab === 'withdraw' && <WithdrawTab balance={balance} amount={withdrawAmount} receiveAmount={usdtReceive} onAmountChange={setWithdrawAmount} onRequest={() => showIntegrationNotice('withdrawal')} data-id="element-71" />}
                {activeTab === 'unstuck' && <UnstuckTab characters={characters} selectedCharacter={selectedCharacter} selectedCharacterId={selectedCharacterId} onCharacterSelect={setSelectedCharacterId} onRequest={() => showIntegrationNotice('unstuck')} data-id="element-72" />}
                {activeTab === 'settings' && <SettingsTab account={account} onRequest={() => showIntegrationNotice('password')} data-id="element-73" />}
              </motion.div>
            </AnimatePresence>

            {notice && <div role="status" data-testid="dashboard-integration-notice" className="mt-6 p-4 rounded-lg bg-gold/10 border border-gold/30 text-gold text-sm" data-id="element-74">
                Esta acción visual está lista para la integración del backend.
                Conecta el callback de {notice} sin cambiar esta interfaz.
              </div>}
          </section>
        </div>
      </div>
    </main>;
}
function OverviewTab({
  account,
  balance,
  characters,
  transactions
}: Pick<DashboardPortalProps, 'account' | 'balance' | 'characters' | 'transactions'>) {
  return <div className="space-y-6" data-id="element-75">
      <h1 className="text-2xl font-bold font-cinzel mb-6" data-id="element-76">Resumen de Cuenta</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-77">
        <article className="glass-panel rounded-xl p-6 border-gold/20 relative overflow-hidden" data-id="element-78">
          <p className="text-gray-400 text-sm mb-1" data-id="element-79">Balance de V Coins</p>
          <h2 data-testid="dashboard-vcoin-balance" data-field="account.vCoinBalance" className="text-4xl font-bold text-gradient-gold font-cinzel mb-2" data-id="element-80">
            {balance.available?.toLocaleString() ?? '—'}
          </h2>
          <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-81">
            Vanguard Coins
          </p>
        </article>
        <article className="glass-panel rounded-xl p-6 border-glowBlue/20" data-id="element-82">
          <p className="text-gray-400 text-sm mb-1" data-id="element-83">Estado de Cuenta</p>
          <div className="flex items-center gap-3 mb-2" data-id="element-84">
            {account.isVip ? <CrownIcon className="w-8 h-8 text-gold" data-id="element-85" /> : <UserIcon className="w-8 h-8 text-gray-400" data-id="element-86" />}
            <h2 data-testid="dashboard-account-status" data-field="account.isVip" className="text-2xl font-bold text-white" data-id="element-87">
              {account.isVip ? 'VIP Activo' : 'Estándar'}
            </h2>
          </div>
          <p data-testid="dashboard-account-email" data-field="account.email" className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-88">
            {account.email ?? '—'}
          </p>
        </article>
      </div>

      <article className="glass-panel rounded-xl p-6" data-testid="dashboard-characters" data-field="account.characters" data-id="element-89">
        <h2 className="text-xl font-bold font-cinzel mb-4 flex items-center gap-2" data-id="element-90">
          <SwordIcon className="w-5 h-5 text-gold" data-id="element-91" /> Tus Personajes
        </h2>
        {characters.length === 0 ? <p data-testid="dashboard-characters-empty" className="text-gray-400" data-id="element-92">
            No hay personajes disponibles.
          </p> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-93">
            {characters.map(character => <div key={character.id} data-testid="dashboard-character-card" data-field={`character.${character.id}`} className="bg-darkerBlue/50 border border-white/5 rounded-lg p-4" data-id="element-94">
                <div className="flex justify-between items-start mb-2" data-id="element-95">
                  <h3 data-testid={`character-name-${character.id}`} className="font-bold text-lg text-white" data-id="element-96">
                    {character.name}
                  </h3>
                  <span data-testid={`character-server-${character.id}`} className="text-xs px-2 py-1 bg-white/10 rounded text-gray-300" data-id="element-97">
                    {character.server ?? '—'}
                  </span>
                </div>
                <p data-testid={`character-class-${character.id}`} className="text-sm text-gold mb-1" data-id="element-98">
                  {character.characterClass}
                </p>
                <div className="flex gap-4 text-sm text-gray-400 mt-3" data-id="element-99">
                  <span data-id="element-100">
                    Nivel:{' '}
                    <strong data-testid={`character-level-${character.id}`} className="text-white" data-id="element-101">
                      {character.level ?? '—'}
                    </strong>
                  </span>
                  <span data-id="element-102">
                    Resets:{' '}
                    <strong data-testid={`character-resets-${character.id}`} className="text-white" data-id="element-103">
                      {character.resets ?? '—'}
                    </strong>
                  </span>
                </div>
              </div>)}
          </div>}
      </article>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-104">
        <article className="glass-panel rounded-xl p-6" data-id="element-105">
          <h2 className="text-xl font-bold font-cinzel mb-4 flex items-center gap-2" data-id="element-106">
            <ShieldIcon className="w-5 h-5 text-glowBlue" data-id="element-107" /> Estadísticas
          </h2>
          <div className="grid grid-cols-3 gap-4 text-center" data-id="element-108">
            <Stat label="Tiempo de Juego" testId="dashboard-play-time" value={account.playTime ?? '—'} data-id="element-109" />
            <Stat label="Asesinatos" testId="dashboard-kills" value={account.kills?.toLocaleString() ?? '—'} color="text-green-400" data-id="element-110" />
            <Stat label="Muertes" testId="dashboard-deaths" value={account.deaths?.toLocaleString() ?? '—'} color="text-red-400" data-id="element-111" />
          </div>
        </article>
        <article className="glass-panel rounded-xl p-6" data-testid="dashboard-transaction-history" data-field="account.transactions" data-id="element-112">
          <h2 className="text-xl font-bold font-cinzel mb-4" data-id="element-113">
            Historial de Transacciones
          </h2>
          {transactions.length === 0 ? <p className="text-gray-400" data-id="element-114">No hay transacciones disponibles.</p> : <div className="space-y-3" data-id="element-115">
              {transactions.map(transaction => <div key={transaction.id} data-testid="dashboard-transaction-row" className="flex justify-between text-sm border-b border-white/5 pb-3" data-id="element-116">
                  <span data-id="element-117">{transaction.type}</span>
                  <span data-id="element-118">
                    {transaction.amount.toLocaleString()} {transaction.currency}
                  </span>
                  <span className="text-gold" data-id="element-119">{transaction.status}</span>
                </div>)}
            </div>}
        </article>
      </div>
    </div>;
}
function PurchaseTab({
  coinPackages,
  selectedPackage,
  onPackageSelect,
  onRequest
}: {
  coinPackages: CoinPackage[];
  selectedPackage: string;
  onPackageSelect: (id: string) => void;
  onRequest: () => void;
}) {
  return <div className="space-y-6" data-id="element-120">
      <div data-id="element-121">
        <h1 className="text-2xl font-bold font-cinzel mb-2" data-id="element-122">Comprar V Coins</h1>
        <p className="text-gray-400" data-id="element-123">
          Selecciona un paquete para potenciar tu experiencia
        </p>
      </div>
      <article className="glass-panel rounded-xl p-6" data-testid="coin-packages" data-field="store.coinPackages" data-id="element-124">
        <h2 className="text-lg font-bold mb-4" data-id="element-125">Paquetes Disponibles</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-126">
          {coinPackages.map(pkg => {
          const selected = selectedPackage === pkg.id;
          return <button type="button" key={pkg.id} data-testid={`coin-package-${pkg.id}`} onClick={() => onPackageSelect(pkg.id)} className={`text-left relative rounded-xl p-5 transition-all border-2 ${selected ? 'bg-gold/5 border-gold shadow-[0_0_20px_rgba(212,175,55,0.15)]' : 'bg-darkerBlue border-white/5 hover:border-gold/30'}`} data-id="element-127">
                {selected && <CheckIcon className="absolute top-3 right-3 w-5 h-5 text-gold" data-id="element-128" />}
                <div className="flex items-center gap-3 mb-2" data-id="element-129">
                  <CrownIcon className="w-6 h-6 text-gold" data-id="element-130" />
                  <h3 className="text-2xl font-bold font-cinzel" data-id="element-131">
                    {pkg.amount.toLocaleString()}
                  </h3>
                </div>
                {pkg.bonus > 0 && <p className="text-sm text-green-400 font-semibold mb-3" data-id="element-132">
                    + {pkg.bonus} Bonus V Coins
                  </p>}
                <div className="mt-4 pt-4 border-t border-white/10 flex justify-between" data-id="element-133">
                  <span className="text-gray-400 text-sm" data-id="element-134">Precio</span>
                  <span className="text-xl font-bold" data-id="element-135">${pkg.priceUsd} USD</span>
                </div>
              </button>;
        })}
        </div>
        <button type="button" onClick={onRequest} data-testid="buy-vcoin-action" className="w-full btn-primary py-4 text-lg" data-id="element-136">
          Comprar Ahora
        </button>
      </article>
    </div>;
}
function WithdrawTab({
  balance,
  amount,
  receiveAmount,
  onAmountChange,
  onRequest
}: {
  balance: VanguardCoinBalance;
  amount: string;
  receiveAmount: string;
  onAmountChange: (value: string) => void;
  onRequest: () => void;
}) {
  return <div className="space-y-6" data-id="element-137">
      <h1 className="text-2xl font-bold font-cinzel" data-id="element-138">Retirar V Coin a USDT</h1>
      <article className="glass-panel rounded-2xl p-6 md:p-10 border-gold/20 max-w-2xl mx-auto" data-testid="withdrawal-panel" data-field="account.withdrawal" data-id="element-139">
        <div className="text-center mb-8" data-id="element-140">
          <h2 className="text-2xl font-bold font-cinzel mb-2" data-id="element-141">
            Retirar V Coin a USDT
          </h2>
          <p className="text-gray-400 text-sm" data-id="element-142">
            Balance de V Coin:{' '}
            <span data-testid="withdraw-vcoin-balance" className="text-gold font-bold" data-id="element-143">
              {balance.available?.toLocaleString() ?? '—'}
            </span>
          </p>
        </div>
        <div className="space-y-6" data-id="element-144">
          <label className="bg-[#1a1510] border border-[#3a2a18] rounded-xl p-4 flex justify-between items-center" data-id="element-145">
            <span className="flex-grow" data-id="element-146">
              <span className="block text-xs text-gray-400 mb-1" data-id="element-147">
                Cantidad a Retirar
              </span>
              <input value={amount} onChange={event => onAmountChange(event.target.value)} type="number" min="0" data-testid="withdrawal-amount-input" className="w-full bg-transparent text-2xl font-bold text-white focus:outline-none" placeholder="0" data-id="element-148" />
            </span>
            <span className="text-gold font-bold text-sm ml-4" data-id="element-149">V COIN</span>
          </label>
          <ArrowDownIcon className="w-6 h-6 text-gold/70 mx-auto" data-id="element-150" />
          <div className="bg-[#1a1510] border border-[#3a2a18] rounded-xl p-4 flex justify-between items-center" data-id="element-151">
            <span className="text-xs text-gray-400" data-id="element-152">Recibirás</span>
            <span data-testid="withdrawal-usdt-preview" data-field="withdrawal.usdtPreview" className="text-2xl font-bold text-white" data-id="element-153">
              {receiveAmount}{' '}
              <span className="text-gray-400 text-sm" data-id="element-154">USDT</span>
            </span>
          </div>
          <button type="button" onClick={onRequest} data-testid="withdrawal-submit" disabled={!amount} className="w-full btn-primary py-4 disabled:opacity-50" data-id="element-155">
            CONFIRMAR RETIRO
          </button>
        </div>
      </article>
    </div>;
}
function UnstuckTab({
  characters,
  selectedCharacter,
  selectedCharacterId,
  onCharacterSelect,
  onRequest
}: {
  characters: GameCharacter[];
  selectedCharacter?: GameCharacter;
  selectedCharacterId: string;
  onCharacterSelect: (id: string) => void;
  onRequest: () => void;
}) {
  return <div className="space-y-6" data-id="element-156">
      <div data-id="element-157">
        <h1 className="text-2xl font-bold font-cinzel mb-2" data-id="element-158">
          Destrabar Personaje
        </h1>
        <p className="text-gray-400" data-id="element-159">
          Mueve tu personaje a la zona segura de Lorencia
        </p>
      </div>
      <article className="glass-panel rounded-xl p-6 max-w-2xl" data-testid="unstuck-character-panel" data-field="account.characters" data-id="element-160">
        <label className="block text-sm font-medium text-gray-300 mb-2" htmlFor="unstuck-character" data-id="element-161">
          Seleccionar Personaje
        </label>
        <select id="unstuck-character" value={selectedCharacterId} onChange={event => onCharacterSelect(event.target.value)} data-testid="unstuck-character-select" className="w-full bg-darkerBlue border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold/50" data-id="element-162">
          {characters.map(character => <option key={character.id} value={character.id} data-id="element-163">
              {character.name} - Nivel {character.level ?? '—'}
            </option>)}
        </select>
        <div className="bg-darkerBlue/50 border border-white/5 rounded-lg p-4 flex items-start gap-4 mt-6" data-id="element-164">
          <AlertTriangleIcon className="w-5 h-5 text-glowBlue flex-shrink-0" data-id="element-165" />
          <div data-id="element-166">
            <p className="text-sm text-gray-400 mb-1" data-id="element-167">Ubicación Actual</p>
            <p data-testid="unstuck-character-location" data-field="character.location" className="font-bold text-white mb-2" data-id="element-168">
              {selectedCharacter?.location ?? '—'}
            </p>
            <p className="text-sm text-gray-300" data-id="element-169">
              Tu personaje será movido a las coordenadas seguras de Lorencia
              (125, 125).
            </p>
            <p className="text-xs text-gold mt-2" data-id="element-170">
              Tiempo de espera: Puedes usar esto una vez cada 5 minutos.
            </p>
          </div>
        </div>
        <button type="button" onClick={onRequest} data-testid="unstuck-character-action" disabled={!selectedCharacter} className="w-full mt-6 bg-transparent border-2 border-glowBlue text-glowBlue hover:bg-glowBlue hover:text-white font-bold py-3 rounded-lg disabled:opacity-50" data-id="element-171">
          Destrabar Personaje
        </button>
      </article>
    </div>;
}
function SettingsTab({
  account,
  onRequest
}: {
  account: AccountProfile;
  onRequest: () => void;
}) {
  return <div className="space-y-6" data-id="element-172">
      <h1 className="text-2xl font-bold font-cinzel" data-id="element-173">Ajustes de Cuenta</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-174">
        <article className="glass-panel rounded-xl p-6 h-fit" data-testid="account-information" data-field="account.profile" data-id="element-175">
          <h2 className="text-lg font-bold mb-4 border-b border-white/10 pb-2" data-id="element-176">
            Información de la Cuenta
          </h2>
          <div className="space-y-4" data-id="element-177">
            <InfoField label="Usuario" testId="settings-username" value={account.username} data-id="element-178" />
            <InfoField label="Correo Electrónico" testId="settings-email" value={account.email ?? '—'} data-id="element-179" />
            <InfoField label="Fecha de Registro" testId="settings-registration-date" value={account.registrationDate ?? '—'} data-id="element-180" />
          </div>
        </article>
        <article className="glass-panel rounded-xl p-6" data-id="element-181">
          <h2 className="text-lg font-bold mb-4 border-b border-white/10 pb-2" data-id="element-182">
            Cambiar Contraseña
          </h2>
          <form onSubmit={event => {
          event.preventDefault();
          onRequest();
        }} className="space-y-4" data-testid="password-change-form" data-id="element-183">
            <input aria-label="Contraseña actual" type="password" className="w-full bg-darkerBlue border border-white/10 rounded-lg px-4 py-2 text-white" placeholder="Contraseña Actual" data-id="element-184" />
            <input aria-label="Nueva contraseña" type="password" className="w-full bg-darkerBlue border border-white/10 rounded-lg px-4 py-2 text-white" placeholder="Nueva Contraseña" data-id="element-185" />
            <input aria-label="Confirmar contraseña" type="password" className="w-full bg-darkerBlue border border-white/10 rounded-lg px-4 py-2 text-white" placeholder="Confirmar Contraseña" data-id="element-186" />
            <button type="submit" data-testid="password-change-submit" className="w-full btn-secondary py-2" data-id="element-187">
              Actualizar Contraseña
            </button>
          </form>
        </article>
      </div>
    </div>;
}
function Stat({
  label,
  testId,
  value,
  color = 'text-white'
}: {
  label: string;
  testId: string;
  value: string;
  color?: string;
}) {
  return <div className="bg-darkerBlue/50 rounded-lg p-4" data-id="element-188">
      <p className="text-gray-400 text-xs uppercase mb-1" data-id="element-189">{label}</p>
      <p data-testid={testId} className={`font-bold text-lg ${color}`} data-id="element-190">
        {value}
      </p>
    </div>;
}
function InfoField({
  label,
  testId,
  value
}: {
  label: string;
  testId: string;
  value: string;
}) {
  return <div data-id="element-191">
      <p className="text-sm text-gray-400 mb-1" data-id="element-192">{label}</p>
      <p data-testid={testId} className="font-medium text-white bg-darkerBlue/50 px-3 py-2 rounded border border-white/5" data-id="element-193">
        {value}
      </p>
    </div>;
}